import type { AxiosError, AxiosResponse } from "axios";
import type {
  ConnectPax8Input,
  Pax8ApplyResult,
  Pax8AutoMapSettings,
  Pax8BulkUpdateSubscriptionMappingsBody,
  Pax8CustomerMappingList,
  Pax8SubscriptionApplyLog,
  Pax8SubscriptionApplyResult,
  Pax8SubscriptionMappingsResponse,
  Pax8SubscriptionStatus,
  Pax8SyncSummary,
} from "shared/types/integrations";
import { axiosClient } from "utilz";
import { apiErrorHandler } from "utilz/helpers";

const BASE_URL = import.meta.env.VITE_APP_INTEGRATION_BASE_URL;

export const connectPax8 = async (data: ConnectPax8Input) => {
  try {
    const url = `${BASE_URL}/auth/pax8`;
    const response = await axiosClient.post(url, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getPax8Settings = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/settings`;
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data as Pax8AutoMapSettings;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const updatePax8Settings = async (settings: Pax8AutoMapSettings) => {
  try {
    const url = `${BASE_URL}/v1/pax8/settings`;
    const response = await axiosClient.put(url, settings);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data as Pax8AutoMapSettings;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const startPax8Sync = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/sync`;
    const response = await axiosClient.post(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 202) {
      return resData;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const syncPax8Customers = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/sync/customers`;
    const response = await axiosClient.post(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data as Pax8SyncSummary;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getPax8CustomerMappings = async (params: {
  status?: string;
  q?: string;
  page?: number;
  limit?: number;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
}) => {
  try {
    const url = `${BASE_URL}/v1/pax8/customer-mappings`;
    const response = await axiosClient.get(url, { params });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data as Pax8CustomerMappingList;
    }
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const resolvePax8Mapping = async (
  mappingId: string,
  body: {
    action: "accept" | "change" | "auto_create" | "un_map";
    deskday_customer_id?: string;
    deskday_customer_name?: string;
  },
) => {
  try {
    const url = `${BASE_URL}/v1/pax8/customer-mappings/${mappingId}`;
    const response = await axiosClient.patch(url, body);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) return resData?.data;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const bulkResolvePax8Mappings = async (body: {
  mapping_ids: string[];
  action: "accept" | "change" | "auto_create" | "un_map";
  deskday_customer_id?: string;
  deskday_customer_name?: string;
}) => {
  try {
    const url = `${BASE_URL}/v1/pax8/customer-mappings/bulk`;
    const response = await axiosClient.patch(url, body);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) return resData?.data;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const applyPax8CustomerMappings = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/customer-mappings/apply`;
    const response = await axiosClient.post(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200)
      return resData?.data as Pax8ApplyResult;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getPax8ImportLog = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/customer-mappings/import-log`;
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200)
      return (resData?.data ?? null) as Pax8ApplyResult | null;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const syncPax8Subscriptions = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/sync/subscriptions`;
    const response = await axiosClient.post(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) return resData?.data;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getPax8SubscriptionMappings = async (params: {
  status: "all" | Pax8SubscriptionStatus;
  q?: string;
  customerId?: string;
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
}): Promise<Pax8SubscriptionMappingsResponse> => {
  try {
    const url = `${BASE_URL}/v1/pax8/subscription-mappings`;
    const response = await axiosClient.get(url, { params });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200)
      return resData?.data as Pax8SubscriptionMappingsResponse;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const updatePax8SubscriptionMapping = async (
  mappingId: string,
  body: {
    invoice_alias?: string;
    recurring_invoice_id?: string;
    recurring_invoice_name?: string;
  },
) => {
  try {
    const url = `${BASE_URL}/v1/pax8/subscription-mappings/${mappingId}`;
    const response = await axiosClient.patch(url, body);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) return resData?.data;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const bulkUpdatePax8SubscriptionMappings = async (
  body: Pax8BulkUpdateSubscriptionMappingsBody,
) => {
  try {
    const url = `${BASE_URL}/v1/pax8/subscription-mappings/bulk`;
    const response = await axiosClient.patch(url, body);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) return resData?.data;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const applyPax8SubscriptionMappings = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/subscription-mappings/apply`;
    const response = await axiosClient.post(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200)
      return resData?.data as Pax8SubscriptionApplyResult;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const getPax8SubscriptionImportLog = async () => {
  try {
    const url = `${BASE_URL}/v1/pax8/subscription-mappings/import-log`;
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200)
      return (resData?.data ?? null) as Pax8SubscriptionApplyLog | null;
    throw response;
  } catch (error: unknown) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};
